
import { Component, Vue, Watch } from "vue-property-decorator";
import { TTab } from "@/models/tabs";
import { SMUser, SMCases, SMActivityLog } from "@/store";

import Tabs from "@/components/Tabs/Tabs.vue";

@Component({
  components: {
    Tabs,
  },
})
export default class ServiceTemplate extends Vue {
  get loading() {
    return SMCases.loading;
  }

  get isTabOpened() {
    return !this.$route.params.tabId;
  }

  get caseId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  get fullPath() {
    return this.$route.fullPath;
  }

  @Watch("caseId", { immediate: true })
  async caseIdChanged(newCaseId: number) {
    if (this.emptyCasesRegex.test(this.fullPath)) return;

    if (SMUser.isRoleSet && !SMUser.isValidationPassed && !SMUser.isExpert) return;

    if (!newCaseId && !SMCases.caseId) {
      const id = Number(localStorage.getItem("case_id"));

      if (!id) return;

      return await this.fetchCase(id);
    }

    if (!newCaseId) return;

    if (SMCases.caseId === newCaseId) return SMCases.updatePageTitle();

    localStorage.setItem("case_id", String(newCaseId));

    SMCases.setCaseId(newCaseId);

    await this.fetchCase(newCaseId);
  }

  async fetchCase(newCaseId: number) {
    if (SMCases.loading === true) return Promise.resolve();

    SMCases.setLoading(true);

    try {
      SMCases.setLoading(true);

      await SMCases.fetchCaseTabs(newCaseId);

      await this.$nextTick();

      await SMCases.fetchDashboard(newCaseId);

      if (SMUser.isExpert) SMActivityLog.fetchtNotes(newCaseId);
    } finally {
      SMCases.setLoading(false);
    }
  }

  emptyCasesRegex = /\/cases\/\d+$/; // ends with cases/{id}

  @Watch("fullPath", { immediate: true })
  async fullPathChanged(path: string) {
    if (!path.includes("cases")) return;

    if (!this.emptyCasesRegex.test(path)) return;

    this.$router.replace(`${path}/dashboard/${SMCases.dashboard.ID || 0}`);
  }

  get tabs(): TTab[] {
    const { caseId: id } = this;

    const path = SMUser.isExpert ? `/expert/cases/${id}` : `/cases/${id}`;

    const candidateTabs = SMCases.caseLayoutTabs.map((tab) => {
      const route = `${tab.tab_type}/${tab.tab_type_id}`;

      const fullRoute = tab.report_template_alias
        ? `${route}?report_template_alias=${tab.report_template_alias}`
        : route;

      return {
        id: tab.ID,
        alias: tab.alias,
        tabTypeId: tab.tab_type_id,
        name: tab.tab_name,
        path: `${path}/${fullRoute}`,
        label: tab.tab_name.toUpperCase(),
        isActive: this.$route.path.includes(route),
        isEditable: tab.is_editable,
        showButton: tab.show_button,
      };
    });

    const expertTabs: TTab[] = [
      {
        name: this.$root.$tc("modules.names.activityLog"),
        path: `${path}/activity`,
        label: this.$root.$tc("modules.names.activityLog").toUpperCase(),
        isActive: this.$route.path.includes("activity"),
        digit: SMActivityLog.count,
      },
    ];

    return SMUser.isExpert ? [...candidateTabs, ...expertTabs] : candidateTabs;
  }

  tabIsEditable = false;
  tabShowSubmitButton = true;
  tabAlias? = "";

  selectTab(tab: TTab): void {
    this.tabIsEditable = !!tab?.isEditable;
    this.tabShowSubmitButton = !!tab?.showButton;
    this.tabAlias = tab?.alias;
  }
}
